<template>
  <div
    :class="variantClass"
    class="!w-full px-3 py-2 rounded-md border flex flex-col md:flex-row items-center gap-4"
  >
    <div class="flex-1 flex items-start gap-x-4">
      <div class="w-12 h-12 flex justify-center items-center">
        <faInfoCircle class="w-9 fill-white" />
      </div>
      <p class="pt-1 text-white text-base">
        {{ $t(text) }}
      </p>
    </div>
    <button
      :disabled="isDisabled"
      class="rounded-md bg-black/10 flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
      @click.prevent="modal = true"
    >
      <div class="flex justify-center items-center h-full bg-black/10 p-2 rounded-s">
        <ocrScanIcon class="fill-white" />
      </div>
      <span class="px-1.5 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
        {{ $t(buttonLabel) }}
      </span>
    </button>
    <ModalVue
      v-model="modal"
      :container-class="[
        [['scanFailed', 'permissionError'].includes(ocrState) ? 'md:w-[42rem]' : 'md:w-[64rem]'],
        'w-[32rem] relative font-universNext rounded max-h-[80vh] md:h-auto'
      ]"
    >
      <template #container>
        <div class="absolute top-4 end-4">
          <button class="rounded border border-black/30 p-1.5" @click.stop.prevent="modal = false">
            <faTimes class="w-4 fill-black/80" />
          </button>
        </div>
        <Transition name="fade" mode="out-in">
          <component
            :is="stepComponent"
            class="w-full md:h-auto py-5 flex flex-col items-center"
            @on-transition="handleTransition"
            @on-complete="handleCompletion"
          />
        </Transition>
      </template>
    </ModalVue>
  </div>
</template>

<script setup>
import faInfoCircle from '@/assets/icons/faInfoCircle.svg?component'
import faTimes from '@/assets/icons/faTimes.svg?component'
import ocrScanIcon from '@/assets/icons/ocrScan.svg?component'
import CameraView from '@/components/Form/OcrBanner/_camera.vue'
import ConfirmView from '@/components/Form/OcrBanner/_confirm.vue'
import PermissionErrorView from '@/components/Form/OcrBanner/_permission_error.vue'
import ScanFailedErrorView from '@/components/Form/OcrBanner/_scan_failed_error.vue'
import InitialView from '@/components/Form/OcrBanner/_initial.vue'
import ModalVue from '@/components/Ui/Modal/index.vue'
import { computed, onBeforeMount, ref, useAttrs, watch } from 'vue'
import { useOcrStore } from '@/stores/ocr'
import { useFormStore } from '@/stores/form'

const ocrStore = useOcrStore()

const { button_label: buttonLabel, ocr_type: ocrType, field_mapping: fieldMapping } = useAttrs()

const props = defineProps({
  text: String,
  variant: {
    type: String,
    default: () => 'info'
  }
})

const stateComponents = {
  initial: InitialView,
  confirm: ConfirmView,
  camera: CameraView,
  scanFailed: ScanFailedErrorView,
  permissionError: PermissionErrorView
}

onBeforeMount(() => {
  ocrStore.ocrType = ocrType
  ocrState.value = 'initial'
})

const modal = ref(false)
const ocrState = ref()

const variants = {
  info: 'border-sky-500 bg-sky-400',
  warning: 'border-orange-500 bg-orange-400',
  danger: 'border-red-500 bg-red-400',
  success: 'border-emerald-500 bg-emerald-400'
}
const variantClass = computed(() => variants[props.variant])

watch(modal, (newValue) => {
  if (!newValue) {
    ocrState.value = 'initial'
  }
})

const stepComponent = computed(() => {
  return stateComponents[ocrState.value]
})

function handleTransition(component) {
  ocrState.value = component
}

function handleCompletion(values) {
  const formStore = useFormStore()
  modal.value = false

  Object.entries(fieldMapping).forEach(([field, value]) => {
    try {
      if ('ref' in value) {
        formStore.form.setFieldValue(value.ref, values[field])
        return
      }
    } catch (error) {
      // it must not be an object
      formStore.form.setFieldValue(field, value)
    }
  })
}
</script>

<script>
export default {
  name: 'OcrBanner'
}
</script>

<style lang="scss" scoped></style>
